import { useState } from 'react'
import type { Result } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import { useAuth } from '../../hooks/use-auth'
import {
  PageHeader,
  DataTable,
  Pagination,
  StatusBadge,
  TabBar,
  ColumnSettingsButton,
  btnPrimary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { ResultReviewModal } from '../workflows/ResultReviewModal'
import { ExternalResultModal } from '../workflows/ExternalResultModal'
import { useTableColumns } from '../../hooks/use-table-columns'

const columns: readonly Column<Result>[] = [
  {
    key: 'barcode',
    header: 'Barcode',
    sortable: true,
    filterable: true,
    render: (r) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {r.isCritical && <StatusBadge status="critical" label="CRIT" />}
        {r.isOnHold && <StatusBadge status="warning" label="ON HOLD" />}
        <strong>{r.barcode || '--'}</strong>
      </span>
    ),
  },
  { key: 'testType', header: 'Test Type', sortable: true, filterable: true, render: (r) => r.testType },
  { key: 'status', header: 'Status', sortable: true, render: (r) => <StatusBadge status={r.status} /> },
  { key: 'value', header: 'Value', sortable: true, render: (r) => r.resultValue || '--' },
  {
    key: 'source',
    header: 'Source',
    sortable: true,
    filterable: true,
    defaultVisible: false,
    render: (r) =>
      r.source === 'external' ? (
        <span title={r.externalLabName ?? ''}>
          <StatusBadge status="info" label="External" />
        </span>
      ) : (
        'Internal'
      ),
  },
  {
    key: 'date',
    header: 'Date',
    sortable: true,
    render: (r) => new Date(r.createdAt).toLocaleDateString(),
    sortValue: (r) => new Date(r.createdAt).getTime(),
  },
]

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resulted', label: 'Resulted' },
  { key: 'verified', label: 'Verified' },
  { key: 'released', label: 'Released' },
  { key: 'critical', label: 'Critical' },
] as const

const WRITE_ROLES = ['admin', 'tech', 'lab_tech'] as const

export function ResultListPage() {
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const [statusTab, setStatusTab] = useState('all')
  const [onHoldFilter, setOnHoldFilter] = useState<string>('')
  const [sourceFilter, setSourceFilter] = useState<string>('')
  const debouncedSearch = useDebounce(search)
  const [selectedResult, setSelectedResult] = useState<Result | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [externalModalOpen, setExternalModalOpen] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('results')

  const canWrite = user && (WRITE_ROLES as readonly string[]).includes(user.role)
  const statusFilter = statusTab === 'all' ? undefined : statusTab === 'critical' ? 'critical' : statusTab

  const { data, total, page, loading, setPage, refresh } = useApiList<Result>({
    path: '/results',
    filters: {
      search: debouncedSearch || undefined,
      status: statusFilter,
      onHold: onHoldFilter || undefined,
      source: sourceFilter || undefined,
    },
  })

  function openResultModal(result: Result) {
    setSelectedResult(result)
    setModalOpen(true)
  }

  return (
    <div>
      <PageHeader
        title="Results"
        count={total}
        onRefresh={refresh}
        actions={
          <>
            {canWrite && (
              <button type="button" style={btnPrimary} onClick={() => setExternalModalOpen(true)}>
                + External Result
              </button>
            )}
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
          </>
        }
      />

      <TabBar tabs={STATUS_TABS} activeTab={statusTab} onTabChange={setStatusTab} />

      <div style={{ display: 'flex', gap: '0.75rem', margin: '0.75rem 0', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...input, width: '250px' }}
          placeholder="Search by barcode..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={onHoldFilter}
          onChange={(e) => setOnHoldFilter(e.target.value)}
          style={{ ...input, width: '160px' }}
        >
          <option value="">All holds</option>
          <option value="true">On Hold</option>
          <option value="false">Not On Hold</option>
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          style={{ ...input, width: '160px' }}
        >
          <option value="">All sources</option>
          <option value="internal">Internal</option>
          <option value="external">External / Send-out</option>
        </select>
      </div>

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={openResultModal}
            emptyMessage="No results found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <ResultReviewModal
        result={selectedResult}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onUpdated={() => refresh()}
      />

      <ExternalResultModal
        isOpen={externalModalOpen}
        onClose={() => setExternalModalOpen(false)}
        onCreated={() => refresh()}
      />
    </div>
  )
}
